import { Injectable } from "@angular/core";
import { ExcelCommandQueueService } from "officejs-helper";
import { letterToColumn } from "./excel-utils";
import { getTableConfig, SheetTableInfo, SourceSheet, TableConfig, TableInfo } from "../../model";
import { WOKRPAPER_SHEETS } from "../../constants/workbook.constants";

export type WorkbookSelectionChangedHandler = (
  eventArgs: Excel.SelectionChangedEventArgs
) => Promise<any>;

type TableBodySnapshot = {
  values: any[][];
  rowIndex: number;
  columnIndex: number;
  rowCount: number;
  columnCount: number;
};

type MappedTableRangeWriteBlock = {
  startColumnIndex: number;
  columnSpan: number;
};

@Injectable({ providedIn: "root" })
export class ExcelHelperService {
  constructor(private queue: ExcelCommandQueueService) {}

  getSheetProtectionState(sheetName: string): Promise<boolean> {
    let sheet: Excel.Worksheet;
    return this.queue.enqueue(
      (context) => {
        sheet = context.workbook.worksheets.getItem(sheetName);
        sheet.load("protection/protected");
      },
      () => {
        return sheet.protection.protected;
      }
    );
  }
  //Being Used
  protectSheet(sheetName: string, password: string): Promise<void> {
    return this.queue.enqueue(async (context) => {
      const sheet = context.workbook.worksheets.getItem(sheetName);
      sheet.protection.protect({ allowAutoFilter: true }, password);
    });
  }
   //Being Used
  unprotectSheet(sheetName: string, password: string): Promise<void> {
    return this.queue.enqueue(async (context) => {
      const sheet = context.workbook.worksheets.getItem(sheetName);
      sheet.protection.unprotect(password);
    });
  }
  getWorkbookProtectionState(): Promise<boolean> {
    return this.queue.enqueue(
      (context) => {
        const protection = context.workbook.protection;
        protection.load("protected");
      },
      (context) => {
        return context.workbook.protection.protected;
      }
    );
  }
   //Being Used
  protectWorkbook(password: string): Promise<void> {
    return this.queue.enqueue(
      (context) => {
        const protection = context.workbook.protection;
        protection.protect(password);
      },
      () => {}
    );
  }
   //Being Used
  unprotectWorkbook(password: string): Promise<void> {
    return this.queue.enqueue(
      (context) => {
        const protection = context.workbook.protection;
        protection.unprotect(password);
      },
      () => {}
    );
  }
  getActiveSheet(): Promise<Excel.Worksheet> {
    let sheet: Excel.Worksheet;
    return this.queue.enqueue(
      (context) => {
        sheet = context.workbook.worksheets.getActiveWorksheet();
        sheet.load("name"); // minimal load
      },
      () => {
        return sheet;
      }
    );
  }

  getActiveSheetName(): Promise<string> {
    let sheet: Excel.Worksheet;
    return this.queue.enqueue(
      (context) => {
        sheet = context.workbook.worksheets.getActiveWorksheet();
        sheet.load('name');
      },
      () => sheet.name
    );
  }

  async isOnReturnsCalendarSheet(): Promise<boolean> {
    const activeSheet = await this.getActiveSheetName();
    return activeSheet.trim() === WOKRPAPER_SHEETS.ReturnsCalendarSheet;
  }

  ///Being used
  getSheetByName(sheetName: string): Promise<Excel.Worksheet> {
    return this.queue.enqueue(
      (context) => {
        const sheet = context.workbook.worksheets.getItem(sheetName);
        sheet.load("name");
      },
      (context) => context.workbook.worksheets.getItem(sheetName)
    );
  }
  activateSheet(sheetName: string): Promise<void> {
    return this.queue.enqueue((context) => {
      const sheet = context.workbook.worksheets.getItem(sheetName);
      sheet.activate();
    });
  }
  registerWorkbookSelectionChangedHandler(
    handler: WorkbookSelectionChangedHandler
  ): Promise<void> {
    return this.queue.enqueue((context) => {
      context.workbook.onSelectionChanged.add(handler);
    });
  }
  unregisterWorkbookSelectionChangedHandler(
    handler: WorkbookSelectionChangedHandler
  ): Promise<void> {
    return this.queue.enqueue((context) => {
      context.workbook.onSelectionChanged.remove(handler);
    });
  }
  getSelectedRangeAddress(): Promise<string> {
    return this.queue.enqueue(
      (context) => {
        const range = context.workbook.getSelectedRange();
        range.load("address");
      },
      (context) => context.workbook.getSelectedRange().address
    );
  }

  ///// Being Used
  setCellValue(
    sheetName: string,
    cellAddress: string,
    value: string | number
  ): Promise<void> {
    return this.queue.enqueue((context) => {
      const sheet = context.workbook.worksheets.getItem(sheetName);
      const range = sheet.getRange(cellAddress);
      range.values = [[value]];
    });
  }
  setCellLocked(
    sheetName: string,
    cellAddress: string,
    isLocked: boolean
  ): Promise<void> {
    return this.queue.enqueue((context) => {
      const sheet = context.workbook.worksheets.getItem(sheetName);
      const range = sheet.getRange(cellAddress);
      range.format.protection.locked = isLocked;
    });
  }

  setCellValues(
    sheetName: string,
    updatesMap: Record<string, string | number>,
    minRow: number,
    maxRow: number
  ): Promise<void> {
    return this.queue.enqueue((context) => {
      const sheet = context.workbook.worksheets.getItem(sheetName);
        
      const columns = Object.keys(updatesMap);
      if (!Array.isArray(columns) || columns.length === 0) return;
      const rowCount = maxRow - minRow + 1;
      if (rowCount <= 0) return;
  
      const leftCol = columns[0];
      const rightCol = columns[columns.length - 1];
      const combinedRange = sheet.getRange(`${leftCol}${minRow}:${rightCol}${maxRow}`);
  
      // Pre-allocate 2D array: rowCount x columns.length
      const values = Array.from({ length: rowCount }, () =>
        new Array(columns.length).fill('')
      );
  
      // Fill the values array based on updatesMap
      columns.forEach((col, cIndex) => {
        const value = updatesMap[col];
          for (let r = 0; r < rowCount; r++)
            values[r][cIndex] = value;
      });
  
      // Apply the values to the range
      combinedRange.values = values;
    });
  }

  setCellValuesForRows(
    sheetName: string,
    updatesMap: Record<string, string | number>,
    rowIndexes: number[]
  ): Promise<void> {
    return this.queue.enqueue((context) => {
      const sheet = context.workbook.worksheets.getItem(sheetName);
      const columns = Object.keys(updatesMap);
      const uniqueRowIndexes = Array.from(new Set(rowIndexes));

      if (columns.length === 0 || uniqueRowIndexes.length === 0) {
        return;
      }

      columns.forEach((column) => {
        if (!/^[A-Z]+$/i.test(column)) {
          throw new Error(`Invalid column address: ${column}`);
        }
      });

      uniqueRowIndexes.forEach((rowIndex) => {
        if (!Number.isInteger(rowIndex) || rowIndex <= 0) {
          throw new Error(`Invalid row index: ${rowIndex}`);
        }

        columns.forEach((column) => {
          const range = sheet.getRange(`${column}${rowIndex}`);
          range.values = [[updatesMap[column]]];
        });
      });
    });
  }

  updateCellProperties(
    sheetName: string,
    cellAddress: string,
    properties: Partial<Excel.RangeFormat>
  ): Promise<void> {
    return this.queue.enqueue((context) => {
      const sheet = context.workbook.worksheets.getItem(sheetName);
      const range = sheet.getRange(cellAddress);
      if (properties.fill) {
        range.format.fill.color =
          properties.fill.color || range.format.fill.color;
      }
      if (properties.font) {
        Object.assign(range.format.font, properties.font);
      }
    });
  }
  updateCellValues(address: string, values: any[][]): Promise<void> {
    return this.queue.enqueue(
      (context) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet();
        const range = sheet.getRange(address);
        range.values = values;
      },
      () => {}
    );
  }

  deleteAllSheetsExcept(sheetNameToKeep: string): Promise<void> {
    return this.queue.enqueue(
      (context) => {
        const sheets = context.workbook.worksheets;
        sheets.load("items/name");
      },
      (context) => {
        const sheets = context.workbook.worksheets;
        const toDelete = sheets.items.filter((s) => s.name !== sheetNameToKeep);
        toDelete.forEach((s) => s.delete());
      }
    );
  }
  getActiveCellDetails(): Promise<{ address: string; value: any }> {
    return this.queue.enqueue(
      (context) => {
        const range = context.workbook.getActiveCell();
        range.load(["address", "values"]);
      },
      (context) => {
        const range = context.workbook.getActiveCell();
        return { address: range.address, value: range.values[0][0] };
      }
    );
  }
  ///Being used
  readCellValues(sheetName: string, cellRange: string): Promise<any[][]> {
    let range: Excel.Range;
    return this.queue.enqueue(
      (context) => {
        const sheet = context.workbook.worksheets.getItem(sheetName);
        range = sheet.getRange(cellRange);
        range.load("values");
      },
      () => {
        return range.values;
      }
    );
  }

  readMultipleCellValues(sheetName: string, cellRanges: string[]): Promise<any[][][]> {
    let ranges: Excel.Range[];
    return this.queue.enqueue(
      async (context) => {
        const sheet = context.workbook.worksheets.getItem(sheetName);
        ranges = cellRanges.map((range) => sheet.getRange(range));
        ranges.forEach((range) => range.load("values")); // Load values for each range
      },
      () => {
        return ranges.map((range) => range.values);
      }
    );
  }
  
  insertRowValues(
    sheetName: string,
    startRowIndex: number,
    values: any[][]
  ): Promise<void> {
    return this.queue.enqueue((context) => {
      const sheet = context.workbook.worksheets.getItem(sheetName);
      const range = sheet.getRangeByIndexes(
        startRowIndex,
        0,
        values.length,
        values[0].length
      );
      range.values = values;
    });
  }
  freezeTopRows(sheetName: string, rowCount: number): Promise<void> {
    return this.queue.enqueue((context) => {
      const sheet = context.workbook.worksheets.getItem(sheetName);
      sheet.freezePanes.freezeRows(rowCount);
    });
  }
  copySheet(sourceSheetName: string, newSheetName: string): Promise<void> {
    return this.queue.enqueue((context) => {
      const sourceSheet = context.workbook.worksheets.getItem(sourceSheetName);
      const copiedSheet = sourceSheet.copy(Excel.WorksheetPositionType.end);
      copiedSheet.name = newSheetName;
    });
  }
  ///Being used
  /**
   * Returns all unique selected rows values from the active  cell selection sheet.
 */
  getSelectedRowsFromActiveSheet(): Promise<any[][]> {
    let sheet: Excel.Worksheet;
    let selectedRange: Excel.Range;
    let usedRange: Excel.Range;
    let selectionAreas: Excel.Range[] = [];
    let rowIndices: number[] = [];
    let colCount: number;
    let rowsRanges: Excel.Range[] = [];

    return this.queue
        .enqueue(
            (context) => {
                sheet = context.workbook.worksheets.getActiveWorksheet();
                selectedRange = context.workbook.getSelectedRange();
                usedRange = sheet.getUsedRange();

                selectedRange.load(["areas", "rowIndex", "rowCount"]);
                usedRange.load("columnCount");
            },
            (context) => {
                colCount = usedRange.columnCount;

            selectionAreas =
              (selectedRange as any).areas && (selectedRange as any).areas.items
                    ? (selectedRange as any).areas.items
                    : [selectedRange];

                const indicesSet = new Set<number>();
                for (const area of selectionAreas) {
                    for (let i = 0; i < area.rowCount; i++) {
                        indicesSet.add(area.rowIndex + i);
                    }
                }
                rowIndices = Array.from(indicesSet).sort((a, b) => a - b);

                rowsRanges = rowIndices.map((rowIdx) =>
                    sheet.getRangeByIndexes(rowIdx, 0, 1, colCount)
                );
                rowsRanges.forEach((r) => r.load("values"));

                return undefined;
            }
        )
        .then(() =>
            this.queue.enqueue(
                () => {},
                () =>
                    rowsRanges.map((r, idx) => [
                        rowIndices[idx] + 1, // Excel row numbers are 1-based
                        ...r.values[0],
                    ])
            )
        );
  }
  //Being Used
  /**
   * Returns selected rows from the active sheet's first table as array of objects keyed by column name.
   */
  async getSelectedRowsObjectsFromActiveSheet(): Promise<any[]> {
    if (!this.supportsSelectedRanges()) {
      return this.getSelectedRowsObjectsFromActiveSheetCore(false);
    }

    try {
      return await this.getSelectedRowsObjectsFromActiveSheetCore(true);
    } catch (error) {
      console.error('Failed to read selected range areas. Falling back to selected range.', error);
      return this.getSelectedRowsObjectsFromActiveSheetCore(false);
    }
  }

  async getSelectedRowsObjectsByColumnNamesFromActiveSheet(columnNames: string[], tableName?: string): Promise<any[]> {
    const supportsSelectedRanges = this.supportsSelectedRanges();

    if (!supportsSelectedRanges) {
      return this.getSelectedRowsObjectsByColumnNamesFromActiveSheetCore(false, columnNames, tableName);
    }

    try {
      return await this.getSelectedRowsObjectsByColumnNamesFromActiveSheetCore(true, columnNames, tableName);
    } catch (error) {
      console.error('[SelectedRowsByColumns] RangeAreas failed. Falling back to selected range.', error);
      return this.getSelectedRowsObjectsByColumnNamesFromActiveSheetCore(false, columnNames, tableName);
    }
  }
  async performApprovalWrite(
    sheetName: string,
    password: string,
    rowIndexes: number[],
    updatesMap: Record<string, string | number>
  ): Promise<void> {
    const columns = Object.keys(updatesMap);

    const sortedRows = Array.from(new Set(rowIndexes)).sort((a, b) => a - b);
    const contiguousBlocks: number[][] = [];
    if (sortedRows.length > 0) {
      let current = [sortedRows[0]];
      for (let i = 1; i < sortedRows.length; i++) {
        if (sortedRows[i] === sortedRows[i - 1] + 1) {
          current.push(sortedRows[i]);
        } else {
          contiguousBlocks.push(current);
          current = [sortedRows[i]];
        }
      }
      contiguousBlocks.push(current);
    }

    await this.queue.runWithContext(async (context) => {
      const sheet = context.workbook.worksheets.getItem(sheetName);
      //first unprotect the sheet
      sheet.protection.unprotect(password);
      await context.sync();

      try {
        if (columns.length > 0 && contiguousBlocks.length > 0) {
          context.application.suspendApiCalculationUntilNextSync();
          context.application.suspendScreenUpdatingUntilNextSync();

          columns.forEach((column) => {
            const value = updatesMap[column];
            contiguousBlocks.forEach((block) => {
              const startRow = block[0];
              const endRow = block[block.length - 1];
              sheet.getRange(`${column}${startRow}:${column}${endRow}`).values =
                Array.from({ length: block.length }, () => [value]);
            });
          });
        }

        sheet.protection.protect({ allowAutoFilter: true }, password);
        await context.sync();
      } catch (writeError) {
        try {
          sheet.protection.protect({ allowAutoFilter: true }, password);
          await context.sync();
        } catch {
          // Ignore recovery failure — surface the original write error
        }
        throw writeError;
      }
    });
  }

  private getSelectedRowsObjectsFromActiveSheetCore(useSelectedRanges: boolean): Promise<any[]> {
    let sheet: Excel.Worksheet;
    let table: Excel.Table;
    let selectedRange: Excel.Range;
    let selectedRanges: Excel.RangeAreas;
    let selectedAreas: Excel.Range[] = [];
    let tableRange: Excel.Range;

    return this.queue.enqueue<any[] | undefined>(
      (context) => {
        sheet = context.workbook.worksheets.getActiveWorksheet();
        sheet.tables.load("items/name");

        if (useSelectedRanges) {
          selectedRanges = context.workbook.getSelectedRanges();
          selectedRanges.areas.load({ rowIndex: true, rowCount: true });
        } else {
          selectedRange = context.workbook.getSelectedRange();
          selectedRange.load(["rowIndex", "rowCount"]);
        }
      },
      () => {
        // Use the first table on the sheet
        if (!sheet.tables.items.length) return [];

        selectedAreas = useSelectedRanges
          ? selectedRanges.areas.items
          : [selectedRange];
        table = sheet.tables.items[0];
        table.load(["columns", "rows"]);
        tableRange = table.getRange();
        tableRange.load(["rowIndex"]);
        return undefined;
      }
    ).then((selectedRows) =>
      selectedRows ?? this.queue.enqueue(
        () => {},
        () => {
          const columns = table.columns.items.map(col => col.name);
          const tableStartRow = tableRange.rowIndex + 1; // +1 skips header
          const selectedWorksheetRowIndexes = this.getSelectedWorksheetRowIndexes(selectedAreas);
          const selectedTableRowIndexes = this.getSelectedTableRowIndexes(
            selectedWorksheetRowIndexes,
            tableStartRow,
            table.rows.items.length
          );

          return selectedTableRowIndexes.map((tableRowIndex) => {
            const rowValues = table.rows.items[tableRowIndex].values[0];
            const sheetRowIndex = tableStartRow + tableRowIndex + 1;
            return this.buildSelectedTableRowObject(columns, rowValues, sheetRowIndex);
          });
        }
      )
    );
  }

  private getSelectedRowsObjectsByColumnNamesFromActiveSheetCore(
    useSelectedRanges: boolean,
    columnNames: string[],
    knownTableName?: string
  ): Promise<any[]> {
    let sheet: Excel.Worksheet;
    let selectedRange: Excel.Range;
    let selectedRanges: Excel.RangeAreas;
    let table: Excel.Table;
    let tableBodyRange: Excel.Range;
    const requestedColumnNames = Array.from(new Set(columnNames));
    let selectedAreasInfo: Array<{ rowIndex: number; rowCount: number }> = [];
    let tableColumnNames: string[] = [];
    let requestedColumns: Array<{
      columnName: string;
      relativeColumnIndex: number;
    }> = [];
    let discoveredTableName = '';
    let selectedTableRowIndexes: number[] = [];
    let tableBodyRowIndex = 0;
    let tableBodyColumnIndex = 0;
    let selectedRowCells: Array<{
      rowIndex: number;
      cells: Record<string, Excel.Range>;
    }> = [];

    const stage1and2: Promise<any[] | undefined> = knownTableName
      ? this.queue.enqueue<any[] | undefined>(
          (context) => {
            sheet = context.workbook.worksheets.getActiveWorksheet();
            table = sheet.tables.getItemOrNullObject(knownTableName);
            table.columns.load("items/name");
            tableBodyRange = table.getDataBodyRange();
            tableBodyRange.load(["rowIndex", "columnIndex", "rowCount"]);

            if (useSelectedRanges) {
              selectedRanges = context.workbook.getSelectedRanges();
              selectedRanges.areas.load({ rowIndex: true, rowCount: true });
            } else {
              selectedRange = context.workbook.getSelectedRange();
              selectedRange.load(["rowIndex", "rowCount"]);
            }
          },
          () => {
            if (table.isNullObject) return [];

            selectedAreasInfo = useSelectedRanges
              ? selectedRanges.areas.items.map((area) => ({ rowIndex: area.rowIndex, rowCount: area.rowCount }))
              : [{ rowIndex: selectedRange.rowIndex, rowCount: selectedRange.rowCount }];

            tableColumnNames = table.columns.items.map((column) => column.name);
            const selectedWorksheetRowIndexes = this.getSelectedWorksheetRowIndexes(selectedAreasInfo);
            selectedTableRowIndexes = this.getSelectedTableRowIndexes(
              selectedWorksheetRowIndexes,
              tableBodyRange.rowIndex,
              tableBodyRange.rowCount
            );
            tableBodyRowIndex = tableBodyRange.rowIndex;
            tableBodyColumnIndex = tableBodyRange.columnIndex;

            requestedColumns = requestedColumnNames.map((columnName) => ({
              columnName,
              relativeColumnIndex: tableColumnNames.findIndex((tableColumnName) => tableColumnName === columnName),
            }));

            return undefined;
          }
        )
      : this.queue.enqueue<any[] | undefined>(
      (context) => {
        sheet = context.workbook.worksheets.getActiveWorksheet();
        sheet.tables.load("items/name");

        if (useSelectedRanges) {
          selectedRanges = context.workbook.getSelectedRanges();
          selectedRanges.areas.load({ rowIndex: true, rowCount: true });
        } else {
          selectedRange = context.workbook.getSelectedRange();
          selectedRange.load(["rowIndex", "rowCount"]);
        }
      },
      () => {
        selectedAreasInfo = useSelectedRanges
          ? selectedRanges.areas.items.map((area) => ({ rowIndex: area.rowIndex, rowCount: area.rowCount }))
          : [{ rowIndex: selectedRange.rowIndex, rowCount: selectedRange.rowCount }];

        if (!sheet.tables.items.length) return [];

        discoveredTableName = sheet.tables.items[0].name;
        return undefined;
      }
    ).then((selectedRows) => {
          if (selectedRows !== undefined) return selectedRows;

      return this.queue.enqueue<any[] | undefined>(
        (context) => {
          sheet = context.workbook.worksheets.getActiveWorksheet();
          table = sheet.tables.getItem(discoveredTableName);
          table.columns.load("items/name");
          tableBodyRange = table.getDataBodyRange();
          tableBodyRange.load(["rowIndex", "columnIndex", "rowCount"]);
        },
        () => {
          tableColumnNames = table.columns.items.map((column) => column.name);
          const selectedWorksheetRowIndexes = this.getSelectedWorksheetRowIndexes(selectedAreasInfo);
          selectedTableRowIndexes = this.getSelectedTableRowIndexes(
            selectedWorksheetRowIndexes,
            tableBodyRange.rowIndex,
            tableBodyRange.rowCount
          );
          tableBodyRowIndex = tableBodyRange.rowIndex;
          tableBodyColumnIndex = tableBodyRange.columnIndex;

          requestedColumns = requestedColumnNames.map((columnName) => ({
            columnName,
            relativeColumnIndex: tableColumnNames.findIndex((tableColumnName) => tableColumnName === columnName),
          }));

          return undefined;
        }
      );
    });
   return stage1and2.then((selectedRows) =>
      selectedRows ?? this.queue.enqueue(
        (context) => {
          sheet = context.workbook.worksheets.getActiveWorksheet();
          selectedRowCells = [];

          selectedTableRowIndexes.forEach((tableRowIndex) => {
            const worksheetRowIndex = tableBodyRowIndex + tableRowIndex;
            const cells: Record<string, Excel.Range> = {};

            requestedColumns.forEach(({ columnName, relativeColumnIndex }) => {
              if (relativeColumnIndex < 0) {
                return;
              }

              const cell = sheet.getRangeByIndexes(
                worksheetRowIndex,
                tableBodyColumnIndex + relativeColumnIndex,
                1,
                1
              );
              cell.load("values");
              cells[columnName] = cell;
            });

            selectedRowCells.push({
              rowIndex: worksheetRowIndex + 1,
              cells,
            });
          });
        },
        () => {
          const rows = selectedRowCells.map(({ rowIndex, cells }) => {
            const row: any = { rowIndex };

            requestedColumns.forEach(({ columnName }) => {
              row[columnName] = cells[columnName]?.values?.[0]?.[0];
            });

            return row;
          });

          return rows;
        }
      )
    );
  }

  private supportsSelectedRanges(): boolean {
    if (typeof Office === 'undefined') {
      return false;
    }

    return Office.context?.requirements?.isSetSupported?.('ExcelApi', '1.9') ?? false;
  }

  private getSelectedWorksheetRowIndexes(
    selectedAreas: Array<Pick<Excel.Range, 'rowIndex' | 'rowCount'>>
  ): number[] {
    const selectedRowIndexes = new Set<number>();

    selectedAreas.forEach((area) => {
      for (let rowOffset = 0; rowOffset < area.rowCount; rowOffset++) {
        selectedRowIndexes.add(area.rowIndex + rowOffset);
      }
    });

    return Array.from(selectedRowIndexes).sort((left, right) => left - right);
  }

  private getSelectedTableRowIndexes(
    selectedWorksheetRowIndexes: number[],
    tableStartRow: number,
    tableRowCount: number
  ): number[] {
    return selectedWorksheetRowIndexes
      .map((worksheetRowIndex) => worksheetRowIndex - tableStartRow)
      .filter((tableRowIndex) => tableRowIndex >= 0 && tableRowIndex < tableRowCount);
  }

  private buildSelectedTableRowObject(
    columns: string[],
    rowValues: any[],
    sheetRowIndex: number
  ): any {
    const rowObj: any = { rowIndex: sheetRowIndex };
    columns.forEach((column, index) => rowObj[column] = rowValues[index]);
    return rowObj;
  }
  hideSheet(sheetName: string): Promise<void> {
    return this.queue.enqueue((context) => {
      const sheet = context.workbook.worksheets.getItem(sheetName);
      sheet.visibility = Excel.SheetVisibility.hidden;
    });
  }
  unhideSheet(sheetName: string): Promise<void> {
    return this.queue.enqueue((context) => {
      const sheet = context.workbook.worksheets.getItem(sheetName);
      sheet.visibility = Excel.SheetVisibility.visible;
    });
  }
  insertRows(
    sheetName: string,
    rowIndex: number,
    rowCount: number
  ): Promise<void> {
    return this.queue.enqueue((context) => {
      const sheet = context.workbook.worksheets.getItem(sheetName);
      const range = sheet.getRangeByIndexes(rowIndex, 0, rowCount, 1);
      range.insert(Excel.InsertShiftDirection.down);
    });
  }
  getSelectedRangeValues(): Promise<any[][]> {
    return this.queue.enqueue(
      (context) => {
        const range = context.workbook.getSelectedRange();
        range.load("values");
      },
      (context) => context.workbook.getSelectedRange().values
    );
  }
  ///Being used
  getUsedRangeValues(sheetName: string): Promise<any[][]> {
    let range: Excel.Range;
    return this.queue.enqueue(
      (context) => {
        const sheet = context.workbook.worksheets.getItem(sheetName);
        range = sheet.getUsedRange();
        range.load("values");
      },
      () => {
        return range.values;
      }
    );
  }

  async getTableValuesByHeaders(sheetName: string, tableName: string, headersToInclude: string[]): Promise<any[][]> {
    let tableRange: Excel.Range;
    const matrix = await this.queue.enqueue(
      (context) => {
        const sheet = context.workbook.worksheets.getItem(sheetName);
        const table = sheet.tables.getItem(tableName);
        tableRange = table.getRange();
        tableRange.load("values");
      },
      () => {
        return tableRange.values;
      }
    );

    if (!matrix || matrix.length === 0) {
      return [headersToInclude];
    }

    const normalizedHeaders = new Set(headersToInclude.map((header) => String(header).trim().toLowerCase()));
    const headerRow = matrix[0] ?? [];

    const selectedIndexes = headerRow
      .map((headerCell, index) => ({
        header: String(headerCell ?? '').trim().toLowerCase(),
        index,
      }))
      .filter((entry) => normalizedHeaders.has(entry.header))
      .map((entry) => entry.index);

    if (selectedIndexes.length === 0) {
      return [headersToInclude];
    }

    return matrix.map((row) => selectedIndexes.map((columnIndex) => row?.[columnIndex]));
  }
  //Being used
  // This is to handle when loading sheet errors out and table is stuck in a state without "totals" row.
  async enableShowTotals(sheetsInfo: SheetTableInfo[]): Promise<void> {
    for (const { sheetName, tableName } of sheetsInfo) {
      await this.queue.enqueue(async (context) => {
        const sheet: Excel.Worksheet = context.workbook.worksheets.getItemOrNullObject(sheetName);
        const table: Excel.Table = sheet.tables.getItemOrNullObject(tableName);
        table.showTotals = true;
      }, () => { });
    }
    await this.queue.flush();
  }

  //Being used
  // This is to handle when any updates are made to the table, we turn off the "totals" row temporarily.
  async disableShowTotals(sheetsInfo: SheetTableInfo[]): Promise<void> {
    for (const { sheetName, tableName } of sheetsInfo) {
      await this.queue.enqueue(async (context) => {
        const sheet: Excel.Worksheet = context.workbook.worksheets.getItemOrNullObject(sheetName);
        const table: Excel.Table = sheet.tables.getItemOrNullObject(tableName);
        table.showTotals = false;
      }, () => { });
    }
    await this.queue.flush();
  }

  async prepareSheetsForLoad(sheetInfos: SheetTableInfo[], password: string): Promise<void> {
    await this.queue.enqueue((context) => {
      for (const sheetInfo of sheetInfos) {
        const sheet = context.workbook.worksheets.getItem(sheetInfo.sheetName);
        const table = sheet.tables.getItem(sheetInfo.tableName);

        sheet.protection.unprotect(password);
        table.showTotals = false;
      }
    });
    await this.queue.flush();
  }

  async restoreSheetsAfterLoad(sheetInfos: SheetTableInfo[], password: string): Promise<void> {
    await this.queue.enqueue((context) => {
      for (const sheetInfo of sheetInfos) {
        const sheet = context.workbook.worksheets.getItem(sheetInfo.sheetName);
        const table = sheet.tables.getItem(sheetInfo.tableName);

        table.showTotals = true;
        if (sheetInfo.shouldProtect) {
          sheet.protection.protect({ allowAutoFilter: true }, password);
        }
      }
    });
    await this.queue.flush();
  }

  async isSheetRestoredAfterLoad(sheetInfo: SheetTableInfo): Promise<boolean> {
    let sheet: Excel.Worksheet;
    let table: Excel.Table;

    try {
      return await this.queue.enqueue(
        (context) => {
          sheet = context.workbook.worksheets.getItem(sheetInfo.sheetName);
          table = sheet.tables.getItem(sheetInfo.tableName);

          sheet.protection.load('protected');
          table.load('showTotals');
        },
        () => table.showTotals && (!sheetInfo.shouldProtect || sheet.protection.protected)
      );
    } catch {
      return false;
    }
  }

  ///Being used
  async initializeTable(sheetTableInfo: SheetTableInfo): Promise<TableInfo> {
    let tableInfo: Partial<TableInfo> = {};
    let table: Excel.Table;
    let range: Excel.Range;
  
    await this.queue.enqueue(
      async (context: Excel.RequestContext) => {
        const { sheetName, tableName } = sheetTableInfo;
        const sheet: Excel.Worksheet = context.workbook.worksheets.getItem(sheetName);
        table = sheet.tables.getItem(tableName);
        range = table.getDataBodyRange();
        table.load("showTotals");
        range.load(["rowIndex", "columnIndex", "columnCount", "rowCount"]);
      },
      () => {
        tableInfo.rowIndex = range.rowIndex;
        tableInfo.columnIndex = range.columnIndex;
        tableInfo.columnCount = range.columnCount;
        tableInfo.rowCount = range.rowCount;
        tableInfo.showTotals = table.showTotals;
      }
    );
  
    await this.queue.flush();
    return tableInfo as TableInfo;
  }
  ///Being Used
  /// Simple approach without batching
  /// this is kept just for reference, do not delete yet.
  async writeDataToTable_bkp(
    tableConfig: TableConfig,
    inputData: any[],
    precomputedTableInfo: TableInfo
  ): Promise<void> {
    const {
      rowIndex,
      columnIndex,
      columnCount,
      rowCount,
      showTotals: hasTotals,
    } = precomputedTableInfo;
    const { sheetType, columnMappings } = tableConfig;
    const { sheetName, tableName } = getTableConfig(sheetType!);

    // Map input data to table columns
    const newData: any[][] = inputData.map((item) => {
      const row: any[] = new Array(columnCount).fill(null);
      for (const mapping of columnMappings) {
        const absCol = letterToColumn(mapping.Column);
        const relIndex = absCol - columnIndex;
        if (relIndex >= 0 && relIndex < columnCount) {
          row[relIndex] = item[mapping.Key] !== undefined ? item[mapping.Key] : null;
        }
      }
      return row;
    });

    await this.queue.enqueue(async (context) => {
      const sheet: Excel.Worksheet = context.workbook.worksheets.getItemOrNullObject(sheetName!);
      const table: Excel.Table = sheet.tables.getItem(tableName);
      
      // Delete all rows except the first
      this.deleteTableRowsRange(sheet, rowIndex + 1, columnIndex, rowCount - 1, columnCount);

      if (newData.length > 0) {
        table.rows.add(undefined, newData); // Insert new rows
        this.deleteTableRowsRange(sheet, rowIndex, columnIndex, 1, columnCount); // Delete the first row after insertion
      }
      
      // Restore totals if needed, footer formulas are restored by default 
      table.showTotals = hasTotals;
    });

    await this.queue.flush();
  }
  ///Being Used
  /// New approach with batching to handle large datasets
  /**
   * Clears all data rows from a table, leaving a single empty placeholder row.
   * Safe to call when API returns empty data — avoids the Excel
   * "would move cells in a table" error caused by deleting the last row.
   */
  async clearTableRows(
    tableConfig: TableConfig,
    precomputedTableInfo: TableInfo
  ): Promise<void> {
    const { rowIndex, columnIndex, columnCount, rowCount } = precomputedTableInfo;
    const { sheetName, tableName } = getTableConfig(tableConfig.sheetType!);

    // Step 1: Delete all rows except the first
    await this.queue.enqueue(async (context) => {
      const sheet: Excel.Worksheet = context.workbook.worksheets.getItemOrNullObject(sheetName!);
      const table: Excel.Table = sheet.tables.getItem(tableName);
      table.clearFilters();
      this.deleteTableRowsRange(sheet, rowIndex + 1, columnIndex, rowCount - 1, columnCount);
    });

    // Step 2: Clear only the data-mapped columns in the remaining first row.
    // Using range.clear(contents) would also erase formula cells (calculated columns).
    // Instead, write null only to the columns declared in columnMappings so that
    // formula columns are left completely untouched. (Bug #174265)
    await this.queue.enqueue(async (context) => {
      const sheet: Excel.Worksheet = context.workbook.worksheets.getItemOrNullObject(sheetName!);
      for (const mapping of tableConfig.columnMappings) {
        const absCol = letterToColumn(mapping.Column); // 0-based absolute column index
        const cell = sheet.getRangeByIndexes(rowIndex, absCol, 1, 1);
        cell.values = [[null]];
      }
    });

    await this.queue.flush();
  }

  async writeDataToTable(
    tableConfig: TableConfig,
    inputData: any[],
    precomputedTableInfo: TableInfo
  ): Promise<void> {
    const BATCH_SIZE = 50000;

    const {
      columnIndex,
      columnCount,
    } = precomputedTableInfo;
    const { sheetType, columnMappings } = tableConfig;
    const { sheetName, tableName } = getTableConfig(sheetType!);
    const shouldLogReplacementDiagnostics = this.isDesktopReplacementRangeWriteCandidate(sheetType);
    const perfLogPrefix = `[${sheetName}_PERF]`;
    const sheetTypeLogName = this.getSheetTypeLogName(sheetType);
    const { mappedColumnMin, mappedColumnMax } = this.getMappedColumnBounds(columnMappings);

    if (this.shouldUseDesktopReplacementRangeWrite(sheetType)) {
      await this.replaceTableRowsWithRangeWrite(
        tableConfig,
        inputData,
        precomputedTableInfo,
        perfLogPrefix,
        mappedColumnMin,
        mappedColumnMax
      );
      return;
    }

    if (shouldLogReplacementDiagnostics) {
      console.log(`${perfLogPrefix} Phase 4-detail: writeMode=tableRowsAdd, sheetType=${sheetTypeLogName}, platform=${Office.context?.platform}, rowCount=${inputData.length}`);
    }
    
    // Map input data to table columns
    const newData: any[][] = inputData.map((item) => {
      const row: any[] = new Array(columnCount).fill(null);
      for (const mapping of columnMappings) {
        const absCol = letterToColumn(mapping.Column);
        const relIndex = absCol - columnIndex;
        if (relIndex >= 0 && relIndex < columnCount) {
          row[relIndex] = this.normalizeCellValue(
            item[mapping.Key] !== undefined ? item[mapping.Key] : null
          );
        }
      }
      return row;
    });

    // When called with no data, delegate to clearTableRows so the table is always left in a clean
    // state regardless of whether the caller handles the empty case itself (addresses the implicit
    // contract risk raised in PR review).
    if (newData.length === 0) {
      await this.clearTableRows(tableConfig, precomputedTableInfo);
      return;
    }

    // Step 1: Clear any active filter so every existing row is addressable, then flush.
    // We intentionally do NOT pre-delete the old rows here. Pre-deleting assuming that a placeholder 
    // sits at a known index is causing the default row to remain when a large data load fails or when a
    // calculated column leaves more than one placeholder behind.
    await this.queue.enqueue(async (context) => {
      const sheet: Excel.Worksheet = context.workbook.worksheets.getItemOrNullObject(sheetName!);
      const table: Excel.Table = sheet.tables.getItem(tableName);
      table.clearFilters();
    });
    await this.queue.flush();

    // Step 2: Append the new data rows in batches at the bottom of the table.
    // The original rows remain at the top and are removed in Step 3.
    // If any batch fails, clearTableRows is called so the table is left empty rather than
    // in a mixed old+partial-new state (addresses transient failure risk raised in PR review).
    try {
      for (let i = 0; i < newData.length; i += BATCH_SIZE) {
        const batch = newData.slice(i, i + BATCH_SIZE);
        await this.queue.enqueue(async (context) => {
          const sheet: Excel.Worksheet = context.workbook.worksheets.getItemOrNullObject(sheetName!);
          const table: Excel.Table = sheet.tables.getItem(tableName);
          context.application.suspendApiCalculationUntilNextSync();
          context.application.suspendScreenUpdatingUntilNextSync();
          table.rows.add(undefined, batch);
        });
        await this.queue.flush();
        console.log(`Appended ${i + batch.length} rows out of ${newData.length} rows to table: ${tableName}`);
      }
    } catch (error) {
      console.error(`Batch append failed for table ${tableName}. Clearing table to avoid mixed data state.`, error);
      await this.clearTableRows(tableConfig, precomputedTableInfo);
      throw error;
    }

    // Step 3: Remove the leftover original/placeholder rows from the TOP of the table.
    // We measure the table's ACTUAL data-body size now (rather than trusting the
    // precomputed count) and delete exactly the excess rows from the top using a reliable
    // range delete. Because the new rows were already appended, the table never becomes
    // empty during deletion, and any number of leftover placeholders is cleared
    // deterministically.
    let bodyRange: Excel.Range;
    let bodyStartRowIndex = 0;
    let bodyColumnIndex = columnIndex;
    let bodyColumnCount = columnCount;
    let actualRowCount = newData.length;
    await this.queue.enqueue(
      async (context) => {
        const sheet: Excel.Worksheet = context.workbook.worksheets.getItemOrNullObject(sheetName!);
        const table: Excel.Table = sheet.tables.getItem(tableName);
        bodyRange = table.getDataBodyRange();
        bodyRange.load(["rowIndex", "columnIndex", "columnCount", "rowCount"]);
      },
      () => {
        bodyStartRowIndex = bodyRange.rowIndex;
        bodyColumnIndex = bodyRange.columnIndex;
        bodyColumnCount = bodyRange.columnCount;
        actualRowCount = bodyRange.rowCount;
      }
    );
    await this.queue.flush();

    const excessRows = actualRowCount - newData.length;
    if (excessRows > 0) {
      await this.queue.enqueue(async (context) => {
        const sheet: Excel.Worksheet = context.workbook.worksheets.getItemOrNullObject(sheetName!);
        context.application.suspendApiCalculationUntilNextSync();
        context.application.suspendScreenUpdatingUntilNextSync();
        this.deleteTableRowsRange(sheet, bodyStartRowIndex, bodyColumnIndex, excessRows, bodyColumnCount);
      });
      await this.queue.flush();
    }
  }

  async replaceTableDataPreservingCalculatedColumns(
    tableConfig: TableConfig,
    inputData: any[],
    precomputedTableInfo: TableInfo
  ): Promise<void> {
    const BATCH_SIZE = 50000;

    const {
      columnIndex,
      columnCount,
      rowCount,
    } = precomputedTableInfo;
    const { sheetType, columnMappings } = tableConfig;
    const { sheetName, tableName } = getTableConfig(sheetType!);
    const desktopWriteBlocks = this.getMappedTableWriteBlocks(tableConfig);
    const perfLogPrefix = `[${sheetName}_PERF]`;
    const sheetTypeLogName = this.getSheetTypeLogName(sheetType);

    if (this.isExcelDesktop() && desktopWriteBlocks) {
      await this.replaceTableRowsPreservingCalculatedColumnsWithRangeWrite(
        tableConfig,
        inputData,
        precomputedTableInfo,
        desktopWriteBlocks,
        perfLogPrefix
      );
      return;
    }

    console.log(`${perfLogPrefix} Phase 4-detail: writeMode=tableRowsAdd, sheetType=${sheetTypeLogName}, platform=${Office.context?.platform}, rowCount=${inputData.length}`);

    const newData: any[][] = inputData.map((item) => {
      const row: any[] = new Array(columnCount).fill(null);
      for (const mapping of columnMappings) {
        const absCol = letterToColumn(mapping.Column);
        const relIndex = absCol - columnIndex;
        if (relIndex >= 0 && relIndex < columnCount) {
          row[relIndex] = this.normalizeCellValue(
            item[mapping.Key] !== undefined ? item[mapping.Key] : null
          );
        }
      }
      return row;
    });

    await this.clearTableRows(tableConfig, precomputedTableInfo);

    if (newData.length === 0) {
      return;
    }

    try {
      for (let i = 0; i < newData.length; i += BATCH_SIZE) {
        const batch = newData.slice(i, i + BATCH_SIZE);
        await this.queue.enqueue(async (context) => {
          const sheet: Excel.Worksheet = context.workbook.worksheets.getItemOrNullObject(sheetName!);
          const table: Excel.Table = sheet.tables.getItem(tableName);
          context.application.suspendApiCalculationUntilNextSync();
          context.application.suspendScreenUpdatingUntilNextSync();
          table.rows.add(undefined, batch);
        });
        await this.queue.flush();
        console.log(`Appended ${i + batch.length} rows out of ${newData.length} rows to table: ${tableName}`);
      }

      await this.queue.enqueue((context) => {
        const sheet = context.workbook.worksheets.getItemOrNullObject(sheetName!);
        context.application.suspendApiCalculationUntilNextSync();
        context.application.suspendScreenUpdatingUntilNextSync();
        this.deleteTableRowsRange(sheet, precomputedTableInfo.rowIndex, columnIndex, 1, columnCount);
      });
      await this.queue.flush();
    } catch (error) {
      console.error(`Credit table append failed for table ${tableName}. Clearing table to avoid mixed data state.`, error);
      await this.clearTableRows(tableConfig, precomputedTableInfo);
      throw error;
    }

    await this.queue.flush();
  }
  ///Being Used
  /**
   * Deletes a range of rows in the table using getRangeByIndexes.
   */
  private deleteTableRowsRange(sheet: Excel.Worksheet, rowIndex: number, columnIndex: number, rowCount: number, columnCount: number): void {
    if (rowCount > 0) {
      const range = sheet.getRangeByIndexes(rowIndex, columnIndex, rowCount, columnCount);
      range.delete(Excel.DeleteShiftDirection.up);
    }
  }

  getTableColumnValues(
    sheetName: string,
    tableName: string,
    columnLetter: string
  ): Promise<any[]> {
    let sheet: Excel.Worksheet;
    let table: Excel.Table;
    let tableRange: Excel.Range;
    let colIndex: number;
    let dataRowCount: number;
    let startRow: number;
    let range: Excel.Range;

    return this.queue
      .enqueue(
        (context) => {
          sheet = context.workbook.worksheets.getItem(sheetName);
          table = sheet.tables.getItem(tableName);
          tableRange = table.getRange();
          table.load("showTotals");
          tableRange.load([
            "columnIndex",
            "rowIndex",
            "rowCount",
            "columnCount",
          ]);
        },
        (context) => {
          colIndex = letterToColumn(columnLetter) - tableRange.columnIndex;

          dataRowCount = tableRange.rowCount - 1 - (table.showTotals ? 1 : 0);
          startRow = tableRange.rowIndex + 1;
          range = sheet.getRangeByIndexes(
            startRow,
            tableRange.columnIndex + colIndex,
            dataRowCount,
            1
          );
          range.load("values");
          return undefined;
        }
      )
      .then(() =>
        this.queue.enqueue(
          () => {},
          () => range.values.map((v) => v[0])
        )
      );
  }
  ///Being Used
  async removeEmptyRowsFromTable(
    sheetName: string,
    tableName: string
  ): Promise<void> {
    let sheet: Excel.Worksheet;
    let table: Excel.Table;
    let tableRange: Excel.Range;
    let tableValues: any[][];
    let rowsToDelete: number[] = [];

    await this.queue.enqueue(
      (context) => {
        sheet = context.workbook.worksheets.getItem(sheetName);
        table = sheet.tables.getItem(tableName);
        tableRange = table.getDataBodyRange();
        tableRange.load(["values", "rowCount"]);
      },
      (context) => {
        tableValues = tableRange.values;
        const rowCount = tableRange.rowCount;
        if (
          rowCount === 1 &&
          tableValues.length === 1 &&
          tableValues[0].every(
            (cell) => cell === null || cell === undefined || cell === ""
          )
        ) {
          // Do not delete the last empty row
          rowsToDelete = [];
        } else {
          // Find rows where all cells are empty, null, or undefined
          for (let i = 0; i < tableValues.length; i++) {
            const row = tableValues[i];
            const isEmpty = row.every(
              (cell) => cell === null || cell === undefined || cell === ""
            );
            if (isEmpty) {
              rowsToDelete.push(i);
            }
          }
        }
      }
    );

    // Delete rows from bottom to top to avoid index shifting
    if (rowsToDelete.length > 0) {
      await this.queue.enqueue((context) => {
        sheet = context.workbook.worksheets.getItem(sheetName);
        table = sheet.tables.getItem(tableName);
        for (let i = rowsToDelete.length - 1; i >= 0; i--) {
          table.rows.getItemAt(rowsToDelete[i]).delete();
        }
      });
    }
    await this.queue.flush();
  }
  //Being Used
/**
 * Removes all rows from the given table where the specified column's value is empty.
 * @param sheetName The name of the worksheet.
 * @param tableName The name of the table.
 * @param columnName The name of the column to check for empty values.
 */
async removeRowsWithEmptyColumnValue(sheetName: string, tableName: string, columnName: string): Promise<void> {
  let sheet: Excel.Worksheet;
  let table: Excel.Table;
  let colIdx: number;
  let rowsToDelete: number[] = [];

  await this.queue.enqueue(
    (context) => {
      sheet = context.workbook.worksheets.getItem(sheetName);
      table = sheet.tables.getItem(tableName);
      table.load(["columns", "rows"]);
    },
    (context) => {
      // Find the column index by name
      colIdx = table.columns.items.findIndex(
        (col) => col.name.trim().toUpperCase() === columnName.trim().toUpperCase()
      );
      if (colIdx === -1) {
        throw new Error(`Column "${columnName}" not found in table "${tableName}".`);
      }

      // Find rows where the specified column is empty
      for (let i = 0; i < table.rows.items.length; i++) {
        const cellValue = table.rows.items[i].values[0][colIdx];
        if (cellValue === null || cellValue === undefined || cellValue === "") {
          rowsToDelete.push(i);
        }
      }
    }
  );

  // Delete rows from bottom to top to avoid index shifting
  if (rowsToDelete.length > 0) {
    await this.queue.enqueue((context) => {
      sheet = context.workbook.worksheets.getItem(sheetName);
      table = sheet.tables.getItem(tableName);
      for (let i = rowsToDelete.length - 1; i >= 0; i--) {
        table.rows.getItemAt(rowsToDelete[i]).delete();
      }
    });
  }

  await this.queue.flush();
}

  /**
   * Removes all rows from the given table where the specified column's value is NOT in the provided set.
   * Used to purge stale/non-RG rows before appending fresh data.
   * @param sheetName The name of the worksheet.
   * @param tableName The name of the table.
   * @param columnIndex The 0-based index of the column to check (0 = col A, 1 = col B, etc.).
   * @param validValues The set of values to retain; rows with any other value are deleted.
   * @returns The column values (at columnIndex) for the rows that were retained after deletion.
   */
  async removeRowsWhereColumnValueNotIn(
    sheetName: string,
    tableName: string,
    columnIndex: number,
    validValues: Set<string>
  ): Promise<string[]> {
    let sheet: Excel.Worksheet;
    let table: Excel.Table;
    let dataRange: Excel.Range;
    let tableValues: any[][];
    let rowsToDelete: number[] = [];
    let keptValues: string[] = [];

    await this.queue.enqueue(
      (context) => {
        sheet = context.workbook.worksheets.getItem(sheetName);
        table = sheet.tables.getItem(tableName);
        dataRange = table.getDataBodyRange();
        dataRange.load(['values', 'rowCount']);
      },
      () => {
        tableValues = dataRange.values;
        const rowCount = dataRange.rowCount;
        // Leave the single placeholder row alone (empty table)
        if (rowCount === 1 && tableValues[0].every((c: any) => c === null || c === undefined || c === '')) {
          rowsToDelete = [];
          keptValues = [];
          return;
        }
        for (let i = 0; i < tableValues.length; i++) {
          const isBlankRow = tableValues[i].every((c: any) => c === null || c === undefined || c === '');
          const cellValue = String(tableValues[i][columnIndex] ?? '').trim();
          if (isBlankRow || !validValues.has(cellValue)) {
            rowsToDelete.push(i);
          } else {
            keptValues.push(cellValue);
          }
        }
      }
    );

    if (rowsToDelete.length > 0) {
      await this.queue.enqueue((context) => {
        sheet = context.workbook.worksheets.getItem(sheetName);
        table = sheet.tables.getItem(tableName);
        console.log(`[${sheetName}_PERF] Recon cleanup: deleteMode=tableRowsDelete, rowCount=${rowsToDelete.length}`);
        for (let i = rowsToDelete.length - 1; i >= 0; i--) {
          table.rows.getItemAt(rowsToDelete[i]).delete();
        }
      });
    }

    await this.queue.flush();
    return keptValues;
  }

  ///Being Used
  /// this is a newer version of appendRowsToTable, the above commented method's logic is modified by Co-pilot in this method.
  /// If this works fine for all the scenarios, we can delete the above commented method.
  async appendRowsToTable(
    tableConfig: TableConfig,
    inputData: any[],
    precomputedTableInfo: TableInfo
  ): Promise<void> {
    const {
      columnIndex,
      columnCount: tableColumnCount,
    } = precomputedTableInfo;
    const { sheetType, columnMappings } = tableConfig;
    const { sheetName, tableName } = getTableConfig(sheetType!);
    const shouldLogAppendDiagnostics = this.isDesktopAppendRangeWriteCandidate(sheetType);
    const perfLogPrefix = `[${sheetName}_PERF]`;
    const sheetTypeLogName = this.getSheetTypeLogName(sheetType);
    const { mappedColumnMin, mappedColumnMax } = this.getMappedColumnBounds(columnMappings);

    if (this.shouldUseDesktopAppendRangeWrite(sheetType)) {
      await this.appendMappedRowsWithRangeWrite(
        tableConfig,
        inputData,
        precomputedTableInfo,
        perfLogPrefix,
        mappedColumnMin,
        mappedColumnMax
      );
      return;
    }

    if (shouldLogAppendDiagnostics) {
      console.log(`${perfLogPrefix} Phase 4b-detail: writeMode=tableRowsAdd, sheetType=${sheetTypeLogName}, platform=${Office.context?.platform}, rowCount=${inputData.length}`);
      console.time(`${perfLogPrefix} Phase 4b-detail: build newData`);
    }

    const newData = this.buildMappedRangeData(
      inputData,
      columnMappings,
      columnIndex,
      tableColumnCount
    );

    if (shouldLogAppendDiagnostics) {
      console.timeEnd(`${perfLogPrefix} Phase 4b-detail: build newData`);
    }

    if (newData.length === 0) {
      console.warn('No data to append.');
      return;
    }

    if (shouldLogAppendDiagnostics) {
      console.time(`${perfLogPrefix} Phase 4b-detail: clearFilters enqueue`);
    }
    await this.queue.enqueue((context) => {
      const sheet = context.workbook.worksheets.getItemOrNullObject(sheetName!);
      const table = sheet.tables.getItem(tableName);
      table.clearFilters();
    });
    if (shouldLogAppendDiagnostics) {
      console.timeEnd(`${perfLogPrefix} Phase 4b-detail: clearFilters enqueue`);
      console.time(`${perfLogPrefix} Phase 4b-detail: rows.add enqueue`);
    }

    await this.queue.enqueue((context) => {
      const sheet = context.workbook.worksheets.getItemOrNullObject(sheetName!);
      const table = sheet.tables.getItem(tableName);

      // Defer ALL formula recalculation and screen updates to the single context.sync()
      // at the end of this batch.
      context.application.suspendApiCalculationUntilNextSync();
      context.application.suspendScreenUpdatingUntilNextSync();

      try {
        table.rows.add(undefined, newData);
        console.log(`Appended ${newData.length} rows to table: ${tableName}`);
      } catch (err) {
        console.error("Error appending rows:", err);
      }
    });

    if (shouldLogAppendDiagnostics) {
      console.timeEnd(`${perfLogPrefix} Phase 4b-detail: rows.add enqueue`);
      console.time(`${perfLogPrefix} Phase 4b-detail: queue.flush`);
    }

    await this.queue.flush();

    if (shouldLogAppendDiagnostics) {
      console.timeEnd(`${perfLogPrefix} Phase 4b-detail: queue.flush`);
    }
  }

  private shouldUseDesktopAppendRangeWrite(sheetType?: SourceSheet): boolean {
    if (!this.isDesktopAppendRangeWriteCandidate(sheetType)) {
      return false;
    }

    return this.isExcelDesktop();
  }

  private shouldUseDesktopReplacementRangeWrite(sheetType?: SourceSheet): boolean {
    if (!this.isDesktopReplacementRangeWriteCandidate(sheetType)) {
      return false;
    }

    return this.isExcelDesktop();
  }

  private isDesktopAppendRangeWriteCandidate(sheetType?: SourceSheet): boolean {
    return sheetType === SourceSheet.ReturnsCalendar || sheetType === SourceSheet.Recon;
  }

  private isDesktopReplacementRangeWriteCandidate(sheetType?: SourceSheet): boolean {
    return sheetType === SourceSheet.RawDataSummary ||
      sheetType === SourceSheet.PaymentExtract ||
      sheetType === SourceSheet.BucketReport ||
      sheetType === SourceSheet.HistoryByReturn ||
      sheetType === SourceSheet.HistoryByState ||
      sheetType === SourceSheet.CreditData;
  }

  private getMappedTableWriteBlocks(
    tableConfig: TableConfig
  ): MappedTableRangeWriteBlock[] | undefined {
    const { columnMappings } = tableConfig;
    if (!columnMappings.length) {
      return undefined;
    }

    const mappedColumnIndexes = columnMappings
      .map((mapping) => letterToColumn(mapping.Column))
      .sort((left, right) => left - right);
    const firstMappedColumnIndex = mappedColumnIndexes[0];
    const lastMappedColumnIndex = mappedColumnIndexes[mappedColumnIndexes.length - 1];

    return [{
      startColumnIndex: firstMappedColumnIndex,
      columnSpan: lastMappedColumnIndex - firstMappedColumnIndex + 1,
    }];
  }

  private getSheetTypeLogName(sheetType?: SourceSheet): string {
    if (sheetType === undefined) {
      return 'Unknown';
    }

    return SourceSheet[sheetType] ?? String(sheetType);
  }

  private isExcelDesktop(): boolean {
    if (typeof Office === 'undefined') {
      return false;
    }

    return Office.context?.platform === Office.PlatformType.PC ||
      Office.context?.platform === Office.PlatformType.Mac;
  }

  private getMappedColumnBounds(columnMappings: TableConfig["columnMappings"]): {
    mappedColumnMin?: number;
    mappedColumnMax?: number;
  } {
    const mappedColumnIndexes = columnMappings.map((mapping) => letterToColumn(mapping.Column));

    return {
      mappedColumnMin: mappedColumnIndexes.length > 0 ? Math.min(...mappedColumnIndexes) : undefined,
      mappedColumnMax: mappedColumnIndexes.length > 0 ? Math.max(...mappedColumnIndexes) : undefined,
    };
  }

  private buildMappedRangeData(
    inputData: any[],
    columnMappings: TableConfig["columnMappings"],
    startColumnIndex: number,
    columnSpan: number
  ): any[][] {
    return inputData.map((item) => {
      const row: any[] = new Array(columnSpan).fill(null);
      for (const mapping of columnMappings) {
        const absoluteColumnIndex = letterToColumn(mapping.Column);
        const relativeColumnIndex = absoluteColumnIndex - startColumnIndex;
        if (relativeColumnIndex >= 0 && relativeColumnIndex < columnSpan) {
          row[relativeColumnIndex] = this.normalizeCellValue(
            item[mapping.Key] !== undefined ? item[mapping.Key] : null
          );
        }
      }
      return row;
    });
  }

  private async getTableBodySnapshot(
    sheetName: string,
    tableName: string,
    tableColumnCount: number,
    perfLogPrefix: string
  ): Promise<TableBodySnapshot> {
    let tableBodyRange: Excel.Range;
    const snapshot: TableBodySnapshot = {
      values: [],
      rowIndex: 0,
      columnIndex: 0,
      rowCount: 0,
      columnCount: tableColumnCount,
    };

    console.time(`${perfLogPrefix} Phase 4b-detail: range load table body`);
    await this.queue.enqueue(
      (context) => {
        const sheet = context.workbook.worksheets.getItemOrNullObject(sheetName);
        const table = sheet.tables.getItem(tableName);
        table.clearFilters();
        tableBodyRange = table.getDataBodyRange();
        tableBodyRange.load(["rowIndex", "columnIndex", "rowCount", "columnCount", "values"]);
      },
      () => {
        snapshot.rowIndex = tableBodyRange.rowIndex;
        snapshot.columnIndex = tableBodyRange.columnIndex;
        snapshot.rowCount = tableBodyRange.rowCount;
        snapshot.columnCount = tableBodyRange.columnCount;
        snapshot.values = tableBodyRange.values;
      }
    );
    await this.queue.flush();
    console.timeEnd(`${perfLogPrefix} Phase 4b-detail: range load table body`);

    return snapshot;
  }

  private async resizeAndWriteRange(
    sheetName: string,
    tableName: string,
    tableBodySnapshot: TableBodySnapshot,
    compactData: any[][],
    mappedColumnMin: number,
    mappedColumnSpan: number,
    perfLogPrefix: string
  ): Promise<void> {
    const hasSingleBlankPlaceholder = tableBodySnapshot.rowCount === 1 &&
      tableBodySnapshot.values.length === 1 &&
      tableBodySnapshot.values[0].every((cell) => cell === null || cell === undefined || cell === '');
    const existingDataRowCount = hasSingleBlankPlaceholder ? 0 : tableBodySnapshot.rowCount;
    const writeStartRowIndex = tableBodySnapshot.rowIndex + existingDataRowCount;
    const targetDataRowCount = existingDataRowCount + compactData.length;
    const headerRowIndex = tableBodySnapshot.rowIndex - 1;

    console.time(`${perfLogPrefix} Phase 4b-detail: range resize/write enqueue`);
    await this.queue.enqueue((context) => {
      const sheet = context.workbook.worksheets.getItemOrNullObject(sheetName);
      const table = sheet.tables.getItem(tableName);
      const resizedTableRange = sheet.getRangeByIndexes(
        headerRowIndex,
        tableBodySnapshot.columnIndex,
        targetDataRowCount + 1,
        tableBodySnapshot.columnCount
      );
      const writeRange = sheet.getRangeByIndexes(
        writeStartRowIndex,
        mappedColumnMin,
        compactData.length,
        mappedColumnSpan
      );

      context.application.suspendApiCalculationUntilNextSync();
      context.application.suspendScreenUpdatingUntilNextSync();

      table.resize(resizedTableRange);
      writeRange.values = compactData;
    });
    console.timeEnd(`${perfLogPrefix} Phase 4b-detail: range resize/write enqueue`);

    console.time(`${perfLogPrefix} Phase 4b-detail: range queue.flush`);
    await this.queue.flush();
    console.timeEnd(`${perfLogPrefix} Phase 4b-detail: range queue.flush`);
  }

  private async appendMappedRowsWithRangeWrite(
    tableConfig: TableConfig,
    inputData: any[],
    precomputedTableInfo: TableInfo,
    perfLogPrefix: string,
    mappedColumnMin?: number,
    mappedColumnMax?: number
  ): Promise<void> {
    const { columnMappings, sheetType } = tableConfig;
    const { sheetName, tableName } = getTableConfig(sheetType!);
    const { columnCount: tableColumnCount } = precomputedTableInfo;

    if (mappedColumnMin === undefined || mappedColumnMax === undefined) {
      console.warn(`${perfLogPrefix} Range write skipped because no mapped columns were found.`);
      return;
    }

    const mappedColumnSpan = mappedColumnMax - mappedColumnMin + 1;
    const sheetTypeLogName = this.getSheetTypeLogName(sheetType);

    console.log(`${perfLogPrefix} Phase 4b-detail: writeMode=range, sheetType=${sheetTypeLogName}, platform=${Office.context?.platform}, rowCount=${inputData.length}`);
    console.time(`${perfLogPrefix} Phase 4b-detail: build compact range data`);
    const compactData = this.buildMappedRangeData(
      inputData,
      columnMappings,
      mappedColumnMin,
      mappedColumnSpan
    );
    console.timeEnd(`${perfLogPrefix} Phase 4b-detail: build compact range data`);

    if (compactData.length === 0) {
      console.warn('No data to append.');
      return;
    }

    const tableBodySnapshot = await this.getTableBodySnapshot(
      sheetName!,
      tableName,
      tableColumnCount,
      perfLogPrefix
    );
    await this.resizeAndWriteRange(
      sheetName!,
      tableName,
      tableBodySnapshot,
      compactData,
      mappedColumnMin,
      mappedColumnSpan,
      perfLogPrefix
    );
  }

  private async replaceTableRowsWithRangeWrite(
    tableConfig: TableConfig,
    inputData: any[],
    precomputedTableInfo: TableInfo,
    perfLogPrefix: string,
    mappedColumnMin?: number,
    mappedColumnMax?: number
  ): Promise<void> {
    const { columnMappings, sheetType } = tableConfig;
    const { sheetName, tableName } = getTableConfig(sheetType!);
    const { columnCount: tableColumnCount } = precomputedTableInfo;

    if (inputData.length === 0) {
      console.log(`${perfLogPrefix} Phase 4-detail: writeMode=range, sheetType=${this.getSheetTypeLogName(sheetType)}, platform=${Office.context?.platform}, rowCount=0`);
      await this.clearTableRows(tableConfig, precomputedTableInfo);
      return;
    }

    if (mappedColumnMin === undefined || mappedColumnMax === undefined) {
      console.warn(`${perfLogPrefix} Range replacement skipped because no mapped columns were found.`);
      return;
    }

    const mappedColumnSpan = mappedColumnMax - mappedColumnMin + 1;
    const sheetTypeLogName = this.getSheetTypeLogName(sheetType);

    console.log(`${perfLogPrefix} Phase 4-detail: writeMode=range, sheetType=${sheetTypeLogName}, platform=${Office.context?.platform}, rowCount=${inputData.length}`);
    console.time(`${perfLogPrefix} Phase 4-detail: build compact range data`);
    const compactData = this.buildMappedRangeData(
      inputData,
      columnMappings,
      mappedColumnMin,
      mappedColumnSpan
    );
    console.timeEnd(`${perfLogPrefix} Phase 4-detail: build compact range data`);

    const tableBodySnapshot = await this.getTableBodySnapshot(
      sheetName!,
      tableName,
      tableColumnCount,
      perfLogPrefix
    );

    console.time(`${perfLogPrefix} Phase 4-detail: range resize/write enqueue`);
    await this.queue.enqueue((context) => {
      const sheet = context.workbook.worksheets.getItemOrNullObject(sheetName!);
      const table = sheet.tables.getItem(tableName);
      const headerRowIndex = tableBodySnapshot.rowIndex - 1;
      const staleRowCount = Math.max(tableBodySnapshot.rowCount - compactData.length, 0);
      const resizedTableRange = sheet.getRangeByIndexes(
        headerRowIndex,
        tableBodySnapshot.columnIndex,
        compactData.length + 1,
        tableBodySnapshot.columnCount
      );
      const writeRange = sheet.getRangeByIndexes(
        tableBodySnapshot.rowIndex,
        mappedColumnMin,
        compactData.length,
        mappedColumnSpan
      );

      context.application.suspendApiCalculationUntilNextSync();
      context.application.suspendScreenUpdatingUntilNextSync();

      table.resize(resizedTableRange);
      writeRange.values = compactData;

      if (staleRowCount > 0) {
        const staleRange = sheet.getRangeByIndexes(
          tableBodySnapshot.rowIndex + compactData.length,
          tableBodySnapshot.columnIndex,
          staleRowCount,
          tableBodySnapshot.columnCount
        );
        staleRange.clear(Excel.ClearApplyTo.all);
      }
    });
    console.timeEnd(`${perfLogPrefix} Phase 4-detail: range resize/write enqueue`);

    try {
      console.time(`${perfLogPrefix} Phase 4-detail: range queue.flush`);
      await this.queue.flush();
      console.timeEnd(`${perfLogPrefix} Phase 4-detail: range queue.flush`);
    } catch (error) {
      console.timeEnd(`${perfLogPrefix} Phase 4-detail: range queue.flush`);
      console.error(`Range replacement failed for table ${tableName}. Clearing table to avoid mixed data state.`, error);
      await this.clearTableRows(tableConfig, precomputedTableInfo);
      throw error;
    }
  }

  private async replaceTableRowsPreservingCalculatedColumnsWithRangeWrite(
    tableConfig: TableConfig,
    inputData: any[],
    precomputedTableInfo: TableInfo,
    writeBlocks: MappedTableRangeWriteBlock[],
    perfLogPrefix: string
  ): Promise<void> {
    const { columnMappings, sheetType } = tableConfig;
    const { sheetName, tableName } = getTableConfig(sheetType!);
    const { columnCount: tableColumnCount } = precomputedTableInfo;
    const sheetTypeLogName = this.getSheetTypeLogName(sheetType);

    console.log(`${perfLogPrefix} Phase 4-detail: writeMode=range, sheetType=${sheetTypeLogName}, platform=${Office.context?.platform}, rowCount=${inputData.length}`);

    if (inputData.length === 0) {
      await this.clearTableRows(tableConfig, precomputedTableInfo);
      return;
    }

    console.time(`${perfLogPrefix} Phase 4-detail: build credit range data`);
    const rangeDataByBlock = writeBlocks.map((writeBlock) => this.buildMappedRangeData(
      inputData,
      columnMappings,
      writeBlock.startColumnIndex,
      writeBlock.columnSpan
    ));
    console.timeEnd(`${perfLogPrefix} Phase 4-detail: build credit range data`);

    try {
      await this.clearTableRows(tableConfig, precomputedTableInfo);

      const tableBodySnapshot = await this.getTableBodySnapshot(
        sheetName!,
        tableName,
        tableColumnCount,
        perfLogPrefix
      );

      console.time(`${perfLogPrefix} Phase 4-detail: credit range resize/write enqueue`);
      await this.queue.enqueue((context) => {
        const sheet = context.workbook.worksheets.getItemOrNullObject(sheetName!);
        const table = sheet.tables.getItem(tableName);
        const headerRowIndex = tableBodySnapshot.rowIndex - 1;
        const resizedTableRange = sheet.getRangeByIndexes(
          headerRowIndex,
          tableBodySnapshot.columnIndex,
          inputData.length + 1,
          tableBodySnapshot.columnCount
        );

        context.application.suspendApiCalculationUntilNextSync();
        context.application.suspendScreenUpdatingUntilNextSync();

        table.resize(resizedTableRange);
        writeBlocks.forEach((writeBlock, index) => {
          const writeRange = sheet.getRangeByIndexes(
            tableBodySnapshot.rowIndex,
            writeBlock.startColumnIndex,
            inputData.length,
            writeBlock.columnSpan
          );
          writeRange.values = rangeDataByBlock[index];
        });
      });
      console.timeEnd(`${perfLogPrefix} Phase 4-detail: credit range resize/write enqueue`);

      console.time(`${perfLogPrefix} Phase 4-detail: credit range queue.flush`);
      await this.queue.flush();
      console.timeEnd(`${perfLogPrefix} Phase 4-detail: credit range queue.flush`);
    } catch (error) {
      console.timeEnd(`${perfLogPrefix} Phase 4-detail: credit range queue.flush`);
      console.error(`Credit table range replacement failed for table ${tableName}. Clearing table to avoid mixed data state.`, error);
      await this.clearTableRows(tableConfig, precomputedTableInfo);
      throw error;
    }
  }
  ///Being Used
  /**
 * Highlights the first row in the active sheet where the value in the specified column matches the given value.
 * @param valueToMatch The value to match in the column
 * @param columnName The Excel table's column name (e.g., 'MasterReturnid', 'EntityMasterReturnId' etc.)
 */
highlightRowByColumnValue(
  valueToMatch: string | number,
  columnName: string,
  tableName: string,
  secondaryColumnName?: string,
  secondaryValueToMatch?: string | number
): Promise<void> {
  return this.findAndActivateRowIndex(
    valueToMatch,
    columnName,
    tableName,
    secondaryColumnName,
    secondaryValueToMatch
  ).then((foundRowIdx) => {
    if (foundRowIdx < 0) return;
    return this.queue.enqueue((context) => {
      const table = context.workbook.tables.getItem(tableName);
      table.rows.getItemAt(foundRowIdx).getRange().select();
    });
  });
}

/**
 * Activates the worksheet that hosts the given table and returns the index of the first
 * table row whose column value(s) match. Returns -1 when no row matches or the column is
 * not found. The activation is committed by this batch's sync before the caller selects.
 */
private findAndActivateRowIndex(
  valueToMatch: string | number,
  columnName: string,
  tableName: string,
  secondaryColumnName?: string,
  secondaryValueToMatch?: string | number
): Promise<number> {
  let table: Excel.Table;
  return this.queue.enqueue<number>(
    (context) => {
      table = context.workbook.tables.getItem(tableName);
      table.worksheet.activate();
      table.columns.load("items/name");
      table.rows.load("items/values");
    },
    () => {
      const columns = table.columns;
      const rows = table.rows;

      // Find the primary column index by name
      const colIdx = columns.items.findIndex(
        (col) => col.name.trim().toUpperCase() === columnName.trim().toUpperCase()
      );
      if (colIdx === -1) return -1;

      // Find the secondary column index if provided
      let secondaryColIdx = -1;
      if (secondaryColumnName) {
        secondaryColIdx = columns.items.findIndex(
          (col) => col.name.trim().toUpperCase() === secondaryColumnName.trim().toUpperCase()
        );
      }

      // Find the row index where the value(s) match
      for (let i = 0; i < rows.items.length; i++) {
        const primaryMatch = rows.items[i].values[0][colIdx] == valueToMatch;
        const secondaryMatch =
          secondaryColIdx !== -1 && secondaryValueToMatch !== undefined
            ? rows.items[i].values[0][secondaryColIdx] == secondaryValueToMatch
            : true; // If no secondary filter, skip this check

        if (primaryMatch && secondaryMatch) {
          return i;
        }
      }
      return -1;
    }
  );
}
/**
 * Protects multiple sheets in bulk.
 * @param sheetNames Array of sheet names to protect.
 * @param password Protection password.
 */
async protectSheetsBulk(sheetNames: string[], password: string): Promise<void> {
  for (const sheetName of sheetNames) {
    await this.queue.enqueue(async (context) => {
      const sheet = context.workbook.worksheets.getItem(sheetName);
      sheet.protection.protect({ allowAutoFilter: true }, password);
    });
  }
  await this.queue.flush();
}

  private normalizeCellValue(value: any): string | number | boolean | Date | null {
    if (value === undefined || value === null) {
      return null;
    }

    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'string') {
      if (!value.trim()) {
        return '';
      }

      const looksLikeDate = /^\d{4}-\d{2}-\d{2}(?:[T\s].*)?$/.test(value);
      if (looksLikeDate) {
        const parsed = new Date(value);
        if (!isNaN(parsed.getTime())) {
          return parsed;
        }
      }

      return value;
    }

    if (typeof value === 'object') {
      if (typeof value.toISOString === 'function') {
        const parsed = new Date(value.toISOString());
        return isNaN(parsed.getTime()) ? null : parsed;
      }

      return JSON.stringify(value);
    }

    return String(value);
  }

/**
 * Unprotects multiple sheets in bulk.
 * @param sheetNames Array of sheet names to unprotect.
 * @param password Protection password.
 */
async unprotectSheetsBulk(sheetNames: string[], password: string): Promise<void> {
  for (const sheetName of sheetNames) {
    await this.queue.enqueue(async (context) => {
      const sheet = context.workbook.worksheets.getItem(sheetName);
      sheet.protection.unprotect(password);
    });
  }
  await this.queue.flush();
  }
  
  
/**
 * Sorts a protected table by temporarily unprotecting, sorting, then re-protecting.
 * Reuses existing protect/unprotect methods for consistency.
 * @param sheetName The sheet containing the table
 * @param tableName The table to sort
 * @param columnIndex The absolute column index to sort by
 * @param ascending True for A-Z, false for Z-A
 * @param password The sheet protection password
 */
async sortProtectedTable(
  sheetName: string,
  tableName: string,
  columnIndex: number,
  ascending: boolean,
  password: string
): Promise<void> {
  // Step 1: Unprotect the sheet
  await this.unprotectSheet(sheetName, password);

  // Step 2: Perform the sort using the provided columnIndex
  await this.queue.enqueue((context) => {
    const sheet = context.workbook.worksheets.getItem(sheetName);
    const table = sheet.tables.getItem(tableName);
    
    const sortField: Excel.SortField = {
      key: columnIndex,
      ascending: ascending,
    };

    table.sort.apply([sortField]);
  });

  await this.queue.flush();

  // Step 3: Re-protect the sheet
  await this.protectSheet(sheetName, password);
}

/**
 * Gets the currently selected column info from the active table.
 * Returns null if no valid column is selected.
 */
async getSelectedTableColumn(): Promise<{ sheetName: string; tableName: string; columnName: string; columnIndex: number } | null> {
  let sheet: Excel.Worksheet;
  let table: Excel.Table;
  let selectedRange: Excel.Range;
  let tableRange: Excel.Range;

  return this.queue.enqueue(
    (context) => {
      sheet = context.workbook.worksheets.getActiveWorksheet();
      selectedRange = context.workbook.getSelectedRange();
      sheet.load("name");
      sheet.tables.load("items/name");
      selectedRange.load(["columnIndex", "rowIndex"]);
    },
    () => {
      // Use the first table on the sheet
      if (!sheet.tables.items.length) return null;
      table = sheet.tables.items[0];
      table.load(["columns", "name"]);
      tableRange = table.getRange();
      tableRange.load(["rowIndex", "columnIndex", "columnCount", "rowCount"]);
      return undefined;
    }
  ).then(() =>
    this.queue.enqueue(
      () => {},
      () => {
        // Check if selection is within table boundaries
        const selCol = selectedRange.columnIndex;
        const selRow = selectedRange.rowIndex;
        const tableStartCol = tableRange.columnIndex;
        const tableEndCol = tableRange.columnIndex + tableRange.columnCount - 1;
        const tableStartRow = tableRange.rowIndex;
        const tableEndRow = tableRange.rowIndex + tableRange.rowCount - 1;

        if (selCol >= tableStartCol && selCol <= tableEndCol && 
            selRow >= tableStartRow && selRow <= tableEndRow) {
          
          // Get column name and absolute column index
          const colIndexInTable = selCol - tableStartCol;
          const columnName = table.columns.items[colIndexInTable].name;

          return {
            sheetName: sheet.name,
            tableName: table.name,
            columnName: columnName,
            columnIndex: selCol
          };
        }

        return null;
      }
    )
  );
}

}
